// src/pages/notebook/NoteBook.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Stack,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  InputAdornment,
  Chip,
  Tooltip,
  Skeleton,
  Autocomplete,
  Card,
  CardContent,
  Fade,
  Badge,
  CircularProgress,
  alpha,
} from "@mui/material";
import { useTheme, styled, keyframes } from "@mui/material/styles";

import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Check";
import CancelIcon from "@mui/icons-material/Close";
import TagIcon from "@mui/icons-material/Label";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import FolderIcon from "@mui/icons-material/Folder";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import NoteIcon from "@mui/icons-material/Note";
import NotesIcon from "@mui/icons-material/Notes";
import BookIcon from "@mui/icons-material/Book";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import EditNoteIcon from "@mui/icons-material/EditNote";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import ReactQuill from "react-quill";
import "quill/dist/quill.snow.css";
import axios from "axios";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { baseUrl } from "../../constants";

dayjs.extend(relativeTime);

type Note = {
  id: number;
  title: string;
  content: string;
  notebookId: number;
  updatedAt?: string;
  tags?: string[];
};

type Notebook = {
  id: number;
  title: string;
  notes: Note[];
};

// ---- helpers
const LS_NOTEBOOK = "notebook:selectedNotebookId";
const LS_NOTE = "notebook:selectedNoteId";

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["link", "code-block"],
    ["clean"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "bullet",
  "align",
  "link",
  "code-block",
];

/* ------------------------------------------------------------------ */
/* Styled Components & Animations                                     */
/* ------------------------------------------------------------------ */

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const GlassCard = styled(Paper)(({ theme }) => ({
  background:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.background.paper, 0.85)
      : alpha("#ffffff", 0.95),
  backdropFilter: "saturate(180%) blur(20px)",
  border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
  borderRadius: 16,
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 8px 32px rgba(0, 0, 0, 0.4)"
      : "0 8px 32px rgba(0, 0, 0, 0.08)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  animation: `${fadeIn} 0.4s ease-out`,
  "&:hover": {
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 12px 48px rgba(0, 0, 0, 0.5)"
        : "0 12px 48px rgba(0, 0, 0, 0.12)",
  },
}));

const StyledPane = styled(GlassCard)(({ theme }) => ({
  height: "calc(100vh - 120px)",
  overflow: "auto",
  display: "flex",
  flexDirection: "column",
  "&::-webkit-scrollbar": {
    width: 8,
  },
  "&::-webkit-scrollbar-track": {
    background: alpha(theme.palette.divider, 0.1),
    borderRadius: 4,
  },
  "&::-webkit-scrollbar-thumb": {
    background: alpha(theme.palette.primary.main, 0.3),
    borderRadius: 4,
    "&:hover": {
      background: alpha(theme.palette.primary.main, 0.5),
    },
  },
}));

const NotebookCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  transition: "all 0.2s ease",
  cursor: "pointer",
  animation: `${slideIn} 0.3s ease-out`,
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 8px 24px rgba(0, 0, 0, 0.3)"
        : "0 8px 24px rgba(0, 0, 0, 0.1)",
    borderColor: alpha(theme.palette.primary.main, 0.3),
  },
  "&.selected": {
    borderColor: theme.palette.primary.main,
    background:
      theme.palette.mode === "dark"
        ? alpha(theme.palette.primary.main, 0.15)
        : alpha(theme.palette.primary.main, 0.08),
    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.2)}`,
  },
}));

const NoteCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  transition: "all 0.2s ease",
  cursor: "pointer",
  marginBottom: 8,
  animation: `${fadeIn} 0.3s ease-out`,
  "&:hover": {
    transform: "translateX(4px)",
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 4px 16px rgba(0, 0, 0, 0.3)"
        : "0 4px 16px rgba(0, 0, 0, 0.1)",
    borderColor: alpha(theme.palette.primary.main, 0.4),
  },
  "&.selected": {
    borderColor: theme.palette.primary.main,
    background:
      theme.palette.mode === "dark"
        ? alpha(theme.palette.primary.main, 0.2)
        : alpha(theme.palette.primary.main, 0.1),
    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.25)}`,
  },
}));

const StatusChip = styled(Chip)(() => ({
  borderRadius: 8,
  fontWeight: 600,
  fontSize: "0.75rem",
  height: 24,
  transition: "all 0.2s ease",
  "&:hover": {
    transform: "scale(1.05)",
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  textTransform: "none",
  fontWeight: 600,
  padding: "10px 24px",
  boxShadow: "none",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 8px 24px rgba(0, 0, 0, 0.4)"
        : "0 8px 24px rgba(0, 0, 0, 0.15)",
  },
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  transition: "all 0.2s ease",
  "&:hover": {
    transform: "scale(1.1)",
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
  },
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "16px 20px",
  borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.primary.main,
    0.05
  )} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
}));

const EmptyState = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(6),
  textAlign: "center",
  color: theme.palette.text.secondary,
  minHeight: 300,
}));

const EditorContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  borderRadius: 12,
  overflow: "hidden",
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  background: theme.palette.background.paper,
  "& .ql-toolbar": {
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    borderBottom: "none",
    background:
      theme.palette.mode === "dark"
        ? alpha("#fff", 0.05)
        : alpha(theme.palette.primary.main, 0.03),
    padding: "12px",
    "& .ql-formats": {
      marginRight: "16px",
    },
  },
  "& .ql-container": {
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    borderTop: "none",
    minHeight: "50vh",
    fontSize: "16px",
    fontFamily: theme.typography.fontFamily,
    "& .ql-editor": {
      padding: "24px",
      minHeight: "50vh",
      "&.ql-blank::before": {
        color: theme.palette.text.secondary,
        fontStyle: "normal",
      },
    },
  },
}));

const NoteBook: React.FC = () => {
  const theme = useTheme();
  const token = localStorage.getItem("accessToken");

  // Data
  const [notebooks, setNotebooks] = useState<Notebook[]>([]);
  const [loading, setLoading] = useState(true);

  // Selection
  const [selectedNotebookId, setSelectedNotebookId] = useState<number | null>(
    Number(localStorage.getItem(LS_NOTEBOOK)) || null
  );
  const [selectedNote, setSelectedNote] = useState<Note | null>(null);

  // Panels / Editors
  const [newNotebookTitle, setNewNotebookTitle] = useState("");
  const [editingNotebookId, setEditingNotebookId] = useState<number | null>(
    null
  );
  const [editingNotebookTitle, setEditingNotebookTitle] = useState("");

  // Search & filters
  const [noteFilter, setNoteFilter] = useState("");
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  // Save states
  const [isSaving, setIsSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Snackbars / dialogs
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "info" | "warning",
  });
  const [confirmDeleteNotebookId, setConfirmDeleteNotebookId] = useState<
    number | null
  >(null);
  const [confirmDeleteNoteId, setConfirmDeleteNoteId] = useState<number | null>(
    null
  );
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // ---- Fetch
  const fetchNotebooks = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${baseUrl}/notebooks`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list: Notebook[] = res.data || [];
      setNotebooks(list);

      // Restore selection if possible
      if (selectedNotebookId && !list.some((n) => n.id === selectedNotebookId)) {
        setSelectedNotebookId(null);
        localStorage.removeItem(LS_NOTEBOOK);
      }

      const storedNoteId = Number(localStorage.getItem(LS_NOTE)) || null;
      if (storedNoteId) {
        const found =
          list.flatMap((n) => n.notes).find((note) => note.id === storedNoteId) ||
          null;
        setSelectedNote(found);
        if (found) setSelectedNotebookId(found.notebookId);
      }
    } catch {
      setSnackbar({
        open: true,
        message: "Failed to load notebooks.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotebooks();
    // warn on unload if dirty
    const beforeUnload = (e: BeforeUnloadEvent) => {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, []);

  // ---- Derived lists
  const currentNotes: Note[] = useMemo(() => {
    const n =
      notebooks.find((nb) => nb.id === selectedNotebookId)?.notes || [];
    const lower = noteFilter.toLowerCase();
    const filterByText = (note: Note) =>
      note.title?.toLowerCase().includes(lower) ||
      (note.content || "").toLowerCase().includes(lower) ||
      (note.tags || []).some((t) => t.toLowerCase().includes(lower));
    const filterByTags = (note: Note) =>
      tagFilter.length === 0 ||
      (note.tags || []).some((t) => tagFilter.includes(t));
    return n
      .filter((note) => filterByText(note) && filterByTags(note))
      .sort((a, b) => {
        const da = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
        const db = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        return db - da; // recent first
      });
  }, [notebooks, selectedNotebookId, noteFilter, tagFilter]);

  const allTags = useMemo(() => {
    const set = new Set<string>();
    notebooks.forEach((nb) =>
      nb.notes.forEach((note) => (note.tags || []).forEach((t) => set.add(t)))
    );
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [notebooks]);

  const selectedNotebook = useMemo(
    () => notebooks.find((nb) => nb.id === selectedNotebookId),
    [notebooks, selectedNotebookId]
  );

  // ---- Selection persistence
  useEffect(() => {
    if (selectedNotebookId) {
      localStorage.setItem(LS_NOTEBOOK, String(selectedNotebookId));
    } else {
      localStorage.removeItem(LS_NOTEBOOK);
    }
  }, [selectedNotebookId]);

  useEffect(() => {
    if (selectedNote?.id) {
      localStorage.setItem(LS_NOTE, String(selectedNote.id));
    } else {
      localStorage.removeItem(LS_NOTE);
    }
  }, [selectedNote?.id]);

  // ---- CRUD: Notebooks
  const createNotebook = async (title: string) => {
    if (!title.trim()) return;
    try {
      const res = await axios.post(
        `${baseUrl}/notebooks`,
        { title },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCreateDialogOpen(false);
      setNewNotebookTitle("");
      await fetchNotebooks();
      setSelectedNotebookId(res.data.id);
      setSnackbar({ open: true, message: "Notebook created successfully!", severity: "success" });
    } catch {
      setSnackbar({ open: true, message: "Failed to create notebook.", severity: "error" });
    }
  };

  const startRenameNotebook = (nb: Notebook) => {
    setEditingNotebookId(nb.id);
    setEditingNotebookTitle(nb.title);
  };

  const updateNotebookTitle = async (id: number, newTitle: string) => {
    try {
      await axios.patch(
        `${baseUrl}/notebooks/${id}`,
        { title: newTitle },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditingNotebookId(null);
      await fetchNotebooks();
      setSnackbar({ open: true, message: "Notebook renamed successfully!", severity: "success" });
    } catch {
      setSnackbar({ open: true, message: "Failed to rename notebook.", severity: "error" });
    }
  };

  const deleteNotebook = async (id: number) => {
    try {
      await axios.delete(`${baseUrl}/notebooks/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (selectedNotebookId === id) {
        setSelectedNotebookId(null);
        setSelectedNote(null);
      }
      await fetchNotebooks();
      setSnackbar({ open: true, message: "Notebook deleted successfully!", severity: "success" });
    } catch {
      setSnackbar({ open: true, message: "Failed to delete notebook.", severity: "error" });
    } finally {
      setConfirmDeleteNotebookId(null);
    }
  };

  // ---- CRUD: Notes
  const createNote = async () => {
    if (!selectedNotebookId) return;
    try {
      const res = await axios.post(
        `${baseUrl}/notes`,
        { notebookId: selectedNotebookId, title: "Untitled", content: "", tags: [] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchNotebooks();
      setSelectedNote(res.data);
      setSnackbar({ open: true, message: "Note created successfully!", severity: "success" });
    } catch {
      setSnackbar({ open: true, message: "Failed to create note.", severity: "error" });
    }
  };

  const duplicateNote = async () => {
    if (!selectedNote) return;
    try {
      const res = await axios.post(
        `${baseUrl}/notes`,
        {
          notebookId: selectedNote.notebookId,
          title: `${selectedNote.title || "Untitled"} (Copy)`,
          content: selectedNote.content || "",
          tags: selectedNote.tags || [],
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchNotebooks();
      setSelectedNote(res.data);
      setSnackbar({ open: true, message: "Note duplicated successfully!", severity: "success" });
    } catch {
      setSnackbar({ open: true, message: "Failed to duplicate note.", severity: "error" });
    }
  };

  const saveNote = async () => {
    if (!selectedNote) return;
    setIsSaving(true);
    try {
      await axios.patch(
        `${baseUrl}/notes/${selectedNote.id}`,
        {
          title: selectedNote.title,
          content: selectedNote.content,
          tags: selectedNote.tags || [],
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchNotebooks();
      setDirty(false);
      const ts = new Date().toISOString();
      setLastSavedAt(ts);
      setSnackbar({ open: true, message: "Note saved successfully!", severity: "success" });
    } catch {
      setSnackbar({ open: true, message: "Failed to save note.", severity: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const scheduleAutosave = () => {
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      if (dirty) saveNote();
    }, 1200); // debounce 1.2s after last change
  };

  const deleteNote = async (id: number) => {
    try {
      await axios.delete(`${baseUrl}/notes/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSnackbar({ open: true, message: "Note deleted successfully!", severity: "success" });
      setSelectedNote(null);
      await fetchNotebooks();
    } catch {
      setSnackbar({ open: true, message: "Failed to delete note.", severity: "error" });
    } finally {
      setConfirmDeleteNoteId(null);
    }
  };

  // ---- Tag change helpers
  const handleTagsChange = (_: any, newTags: string[]) => {
    if (!selectedNote) return;
    setSelectedNote({ ...selectedNote, tags: newTags });
    setDirty(true);
    scheduleAutosave();
  };

  // ---- Keyboard shortcuts
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().includes("MAC");
      const meta = isMac ? e.metaKey : e.ctrlKey;

      // Save
      if (meta && e.key.toLowerCase() === "s") {
        e.preventDefault();
        void saveNote();
      }

      // Focus search
      if (meta && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNote, dirty]);

  // ---- Render
  return (
    <Box
      sx={{
        p: 3,
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.primary.main,
          0.02
        )} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
      }}
    >
      <Box display="flex" gap={2} width="100%">
        {/* Notebooks Pane */}
        <StyledPane sx={{ width: 300, flexShrink: 0 }}>
          <SectionHeader>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flex: 1,
              }}
            >
              <MenuBookIcon
                sx={{
                  color: theme.palette.primary.main,
                  fontSize: 24,
                }}
              />
              <Typography variant="h6" fontWeight={800}>
                Notebooks
              </Typography>
              <Badge
                badgeContent={notebooks.length}
                color="primary"
                sx={{ ml: 1 }}
              />
            </Box>
            <Tooltip title="Create new notebook">
              <StyledIconButton
                size="small"
                onClick={() => setCreateDialogOpen(true)}
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                  },
                }}
              >
                <AddIcon />
              </StyledIconButton>
            </Tooltip>
          </SectionHeader>

          <Box sx={{ p: 2, flex: 1, overflow: "auto" }}>
            {loading ? (
              <Stack spacing={1.5}>
                {[1, 2, 3].map((i) => (
                  <Skeleton
                    key={i}
                    variant="rounded"
                    height={80}
                    sx={{ borderRadius: 2 }}
                  />
                ))}
              </Stack>
            ) : notebooks.length === 0 ? (
              <EmptyState>
                <BookIcon
                  sx={{
                    fontSize: 64,
                    color: theme.palette.text.secondary,
                    mb: 2,
                    opacity: 0.5,
                  }}
                />
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  No Notebooks Yet
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3, maxWidth: 200 }}
                >
                  Create your first notebook to start organizing your notes
                </Typography>
                <StyledButton
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateDialogOpen(true)}
                >
                  Create Notebook
                </StyledButton>
              </EmptyState>
            ) : (
              <Stack spacing={1.5}>
                {notebooks.map((nb) => (
                  <Fade in key={nb.id} timeout={300}>
                    <NotebookCard
                      className={selectedNotebookId === nb.id ? "selected" : ""}
                      onClick={() => {
                        setSelectedNotebookId(nb.id);
                        setSelectedNote(null);
                      }}
                    >
                      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            gap: 1,
                          }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            {editingNotebookId === nb.id ? (
                              <TextField
                                size="small"
                                value={editingNotebookTitle}
                                onChange={(e) =>
                                  setEditingNotebookTitle(e.target.value)
                                }
                                fullWidth
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    updateNotebookTitle(
                                      nb.id,
                                      editingNotebookTitle.trim()
                                    );
                                  } else if (e.key === "Escape") {
                                    setEditingNotebookId(null);
                                  }
                                }}
                                sx={{ mb: 1 }}
                              />
                            ) : (
                              <>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    mb: 0.5,
                                  }}
                                >
                                  {selectedNotebookId === nb.id ? (
                                    <FolderOpenIcon
                                      sx={{
                                        fontSize: 20,
                                        color: theme.palette.primary.main,
                                      }}
                                    />
                                  ) : (
                                    <FolderIcon
                                      sx={{
                                        fontSize: 20,
                                        color: theme.palette.text.secondary,
                                      }}
                                    />
                                  )}
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight={700}
                                    sx={{
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {nb.title}
                                  </Typography>
                                </Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  <NotesIcon sx={{ fontSize: 14 }} />
                                  {nb.notes?.length || 0} notes
                                </Typography>
                              </>
                            )}
                          </Box>
                          {editingNotebookId === nb.id ? (
                            <Box sx={{ display: "flex", gap: 0.5 }}>
                              <Tooltip title="Save">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    updateNotebookTitle(
                                      nb.id,
                                      editingNotebookTitle.trim()
                                    )
                                  }
                                  color="primary"
                                >
                                  <SaveIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Cancel">
                                <IconButton
                                  size="small"
                                  onClick={() => setEditingNotebookId(null)}
                                >
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                display: "flex",
                                gap: 0.5,
                                opacity: 0.7,
                                "&:hover": { opacity: 1 },
                              }}
                            >
                              <Tooltip title="Rename">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    startRenameNotebook(nb);
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setConfirmDeleteNotebookId(nb.id);
                                  }}
                                  color="error"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </Box>
                      </CardContent>
                    </NotebookCard>
                  </Fade>
                ))}
              </Stack>
            )}
          </Box>
        </StyledPane>

        {/* Notes Pane */}
        <StyledPane sx={{ width: 360, flexShrink: 0 }}>
          <SectionHeader>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flex: 1,
              }}
            >
              <NoteIcon
                sx={{
                  color: theme.palette.secondary.main,
                  fontSize: 24,
                }}
              />
              <Typography variant="h6" fontWeight={800}>
                Notes
              </Typography>
              {selectedNotebook && (
                <Badge
                  badgeContent={currentNotes.length}
                  color="secondary"
                  sx={{ ml: 1 }}
                />
              )}
            </Box>
            <Tooltip
              title={selectedNotebookId ? "Create new note" : "Select a notebook first"}
            >
              <span>
                <StyledIconButton
                  size="small"
                  onClick={createNote}
                  disabled={!selectedNotebookId}
                  sx={{
                    bgcolor: alpha(theme.palette.secondary.main, 0.1),
                    "&:hover": {
                      bgcolor: alpha(theme.palette.secondary.main, 0.2),
                    },
                    "&:disabled": {
                      opacity: 0.5,
                    },
                  }}
                >
                  <AddIcon />
                </StyledIconButton>
              </span>
            </Tooltip>
          </SectionHeader>

          <Box sx={{ p: 2 }}>
            <TextField
              inputRef={searchInputRef}
              placeholder="Search notes..."
              size="small"
              fullWidth
              value={noteFilter}
              onChange={(e) => setNoteFilter(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: noteFilter && (
                  <InputAdornment position="end">
                    <Tooltip title="Clear search">
                      <IconButton
                        size="small"
                        onClick={() => setNoteFilter("")}
                      >
                        <CancelIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />

            {allTags.length > 0 && (
              <Autocomplete
                multiple
                size="small"
                options={allTags}
                value={tagFilter}
                onChange={(_, v) => setTagFilter(v)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Filter by tags"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <LocalOfferIcon fontSize="small" />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      mb: 2,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option}
                      label={option}
                      size="small"
                      sx={{ borderRadius: 1 }}
                    />
                  ))
                }
              />
            )}

            {selectedNotebookId == null ? (
              <EmptyState>
                <FolderIcon
                  sx={{
                    fontSize: 64,
                    color: theme.palette.text.secondary,
                    mb: 2,
                    opacity: 0.5,
                  }}
                />
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Select a Notebook
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Choose a notebook from the left to view its notes
                </Typography>
              </EmptyState>
            ) : currentNotes.length === 0 ? (
              <EmptyState>
                <NoteIcon
                  sx={{
                    fontSize: 64,
                    color: theme.palette.text.secondary,
                    mb: 2,
                    opacity: 0.5,
                  }}
                />
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {noteFilter || tagFilter.length > 0
                    ? "No Notes Found"
                    : "No Notes Yet"}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {noteFilter || tagFilter.length > 0
                    ? "Try adjusting your search or filters"
                    : "Create your first note to get started"}
                </Typography>
                {(noteFilter || tagFilter.length > 0) && (
                  <StyledButton
                    variant="outlined"
                    onClick={() => {
                      setNoteFilter("");
                      setTagFilter([]);
                    }}
                  >
                    Clear Filters
                  </StyledButton>
                )}
                {!noteFilter && tagFilter.length === 0 && (
                  <StyledButton
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={createNote}
                  >
                    Create Note
                  </StyledButton>
                )}
              </EmptyState>
            ) : (
              <Stack spacing={1}>
                {currentNotes.map((note) => (
                  <Fade in key={note.id} timeout={300}>
                    <NoteCard
                      className={
                        selectedNote?.id === note.id ? "selected" : ""
                      }
                      onClick={() => setSelectedNote(note)}
                    >
                      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                        <Typography
                          variant="subtitle1"
                          fontWeight={700}
                          sx={{
                            mb: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {note.title || "Untitled"}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mb: 1.5,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            minHeight: 40,
                          }}
                          dangerouslySetInnerHTML={{
                            __html:
                              (note.content || "")
                                .replace(/<[^>]+>/g, " ")
                                .trim() || "No content",
                          }}
                        />
                        {(note.tags || []).length > 0 && (
                          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 1 }}>
                            {(note.tags || []).slice(0, 3).map((t, i) => (
                              <Chip
                                key={i}
                                size="small"
                                label={t}
                                sx={{
                                  height: 20,
                                  fontSize: "0.7rem",
                                  borderRadius: 1,
                                }}
                              />
                            ))}
                            {(note.tags || []).length > 3 && (
                              <Chip
                                size="small"
                                label={`+${(note.tags || []).length - 3}`}
                                sx={{
                                  height: 20,
                                  fontSize: "0.7rem",
                                  borderRadius: 1,
                                }}
                              />
                            )}
                          </Box>
                        )}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            mt: 1,
                          }}
                        >
                          <AccessTimeIcon
                            sx={{ fontSize: 14, color: theme.palette.text.secondary }}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "0.7rem" }}
                          >
                            {note.updatedAt
                              ? dayjs(note.updatedAt).fromNow()
                              : "Never saved"}
                          </Typography>
                        </Box>
                      </CardContent>
                    </NoteCard>
                  </Fade>
                ))}
              </Stack>
            )}
          </Box>
        </StyledPane>

        {/* Editor Pane */}
        <StyledPane sx={{ flex: 1 }}>
          <SectionHeader>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
              <EditNoteIcon
                sx={{
                  color: theme.palette.success.main,
                  fontSize: 24,
                }}
              />
              <Typography variant="h6" fontWeight={800}>
                Editor
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 2 }}>
                {dirty && (
                  <StatusChip
                    icon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
                    label="Editing..."
                    color="warning"
                    size="small"
                  />
                )}
                {isSaving && (
                  <StatusChip
                    icon={
                      <CircularProgress size={12} sx={{ color: "inherit" }} />
                    }
                    label="Saving..."
                    color="info"
                    size="small"
                  />
                )}
                {!dirty && !isSaving && lastSavedAt && (
                  <StatusChip
                    icon={<CloudDoneIcon sx={{ fontSize: 14 }} />}
                    label={`Saved ${dayjs(lastSavedAt).fromNow()}`}
                    color="success"
                    size="small"
                  />
                )}
                {!dirty && !isSaving && !lastSavedAt && selectedNote && (
                  <StatusChip label="Ready" color="default" size="small" />
                )}
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Tooltip title="Duplicate note (Ctrl+D)">
                <span>
                  <StyledIconButton
                    onClick={duplicateNote}
                    disabled={!selectedNote}
                    size="small"
                  >
                    <ContentCopyIcon fontSize="small" />
                  </StyledIconButton>
                </span>
              </Tooltip>
              <Tooltip title="Delete note">
                <span>
                  <StyledIconButton
                    color="error"
                    onClick={() =>
                      selectedNote && setConfirmDeleteNoteId(selectedNote.id)
                    }
                    disabled={!selectedNote}
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </StyledIconButton>
                </span>
              </Tooltip>
              <StyledButton
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={saveNote}
                disabled={!selectedNote || isSaving}
                size="small"
              >
                Save
              </StyledButton>
            </Box>
          </SectionHeader>

          <Box sx={{ p: 3, flex: 1, display: "flex", flexDirection: "column", overflow: "auto" }}>
            {selectedNote ? (
              <>
                <TextField
                  variant="outlined"
                  placeholder="Note title..."
                  value={selectedNote.title}
                  onChange={(e) => {
                    setSelectedNote({ ...selectedNote, title: e.target.value });
                    setDirty(true);
                    scheduleAutosave();
                  }}
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      fontSize: "1.5rem",
                      fontWeight: 700,
                      "& input": {
                        py: 1.5,
                      },
                    },
                  }}
                />

                <Autocomplete
                  multiple
                  freeSolo
                  size="small"
                  options={allTags}
                  value={selectedNote.tags || []}
                  onChange={handleTagsChange}
                  renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option}
                        {...getTagProps({ index })}
                        key={index}
                        sx={{ borderRadius: 1 }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Add tags..."
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <TagIcon fontSize="small" />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                      sx={{
                        mb: 2,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                        },
                      }}
                    />
                  )}
                />

                <EditorContainer>
                  <ReactQuill
                    value={selectedNote.content}
                    onChange={(value) => {
                      setSelectedNote({ ...selectedNote, content: value });
                      setDirty(true);
                      scheduleAutosave();
                    }}
                    modules={quillModules}
                    formats={quillFormats}
                    theme="snow"
                  />
                </EditorContainer>

                <Box
                  sx={{
                    mt: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    pt: 2,
                    borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    <KeyboardIcon sx={{ fontSize: 14, verticalAlign: "middle", mr: 0.5 }} />
                    Press Ctrl+S to save, Ctrl+K to search
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Last updated:{" "}
                    {selectedNote.updatedAt
                      ? dayjs(selectedNote.updatedAt).format("MMM D, YYYY [at] h:mm A")
                      : "Never"}
                  </Typography>
                </Box>
              </>
            ) : (
              <EmptyState sx={{ minHeight: "60vh" }}>
                <EditNoteIcon
                  sx={{
                    fontSize: 80,
                    color: theme.palette.text.secondary,
                    mb: 2,
                    opacity: 0.3,
                  }}
                />
                <Typography variant="h5" fontWeight={600} gutterBottom>
                  Select a Note
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400 }}>
                  Choose a note from the list to view and edit, or create a new note to get started
                </Typography>
                <StyledButton
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={createNote}
                  disabled={!selectedNotebookId}
                  size="large"
                >
                  Create New Note
                </StyledButton>
              </EmptyState>
            )}
          </Box>
        </StyledPane>
      </Box>

      {/* Create notebook dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            minWidth: 400,
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            pb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <BookIcon color="primary" />
            <Typography variant="h6" fontWeight={700}>
              Create New Notebook
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            autoFocus
            label="Notebook Title"
            fullWidth
            value={newNotebookTitle}
            onChange={(e) => setNewNotebookTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && newNotebookTitle.trim()) {
                createNotebook(newNotebookTitle);
              }
            }}
            placeholder="Enter notebook name..."
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
          />
        </DialogContent>
        <DialogActions
          sx={{
            p: 2,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Button
            onClick={() => setCreateDialogOpen(false)}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <StyledButton
            variant="contained"
            onClick={() => createNotebook(newNotebookTitle)}
            disabled={!newNotebookTitle.trim()}
          >
            Create
          </StyledButton>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Notebook */}
      <Dialog
        open={!!confirmDeleteNotebookId}
        onClose={() => setConfirmDeleteNotebookId(null)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            minWidth: 400,
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            pb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DeleteIcon color="error" />
            <Typography variant="h6" fontWeight={700}>
              Delete Notebook
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <DialogContentText>
            Are you sure you want to delete this notebook? This will permanently
            delete the notebook and all its notes. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
            p: 2,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Button
            onClick={() => setConfirmDeleteNotebookId(null)}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <StyledButton
            color="error"
            variant="contained"
            onClick={() =>
              confirmDeleteNotebookId && deleteNotebook(confirmDeleteNotebookId)
            }
          >
            Delete
          </StyledButton>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Note */}
      <Dialog
        open={!!confirmDeleteNoteId}
        onClose={() => setConfirmDeleteNoteId(null)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            minWidth: 400,
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            pb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DeleteIcon color="error" />
            <Typography variant="h6" fontWeight={700}>
              Delete Note
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <DialogContentText>
            Are you sure you want to delete this note? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
            p: 2,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Button
            onClick={() => setConfirmDeleteNoteId(null)}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <StyledButton
            color="error"
            variant="contained"
            onClick={() => confirmDeleteNoteId && deleteNote(confirmDeleteNoteId)}
          >
            Delete
          </StyledButton>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: "100%",
            borderRadius: 2,
            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.2)",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NoteBook;
